
import React, { useState, useEffect, useRef } from 'react';
import { Card, Button, Badge, Input, Select } from '../components/UI';
import { Ticket, TicketStatus, TicketType, UserRole, User } from '../types';
import { MessageSquare, Video, Shield, Clock, FileText, CheckCircle, XCircle, Plus, Gavel, AlertTriangle, User as UserIcon, Send, Flag, Save, BookOpen, Trash2, Users, Archive, Inbox, Newspaper, Lock, Unlock, ExternalLink, Timer, AlertOctagon, RefreshCw, X, ChevronRight, Copy, FilePlus, BellRing } from 'lucide-react';
import { apiRequest } from '../utils/api';
import { useAuth } from '../components/AuthContext';
import { useLeague } from '../components/LeagueContext';
import { API_BASE_URL } from '../config';
import { Link } from 'react-router-dom';
import { PageHero } from '../components/PageHero';

// Extended Interface explicitly defined to avoid type issues
interface TicketExtended {
    id: string;
    eventId: string;
    authorId: string;
    accusedId?: string;
    type: TicketType;
    status: TicketStatus;
    lap?: number;
    corner?: string;
    description: string;
    videoUrl?: string;
    createdAt: string; // TS standard

    // API specific
    created_at?: string; // PHP response standard
    authorName: string;
    accusedName?: string;
    trackLocation: string;
    decision?: string; 
    discussionLocked?: boolean; 
    video_url?: string;
    eventDate?: string;
    
    participants?: {
        user_id: string;
        role: 'ACCUSER' | 'ACCUSED' | 'WITNESS';
        username: string;
        avatar_url?: string;
        teamName?: string;
        teamColor?: string;
    }[];
    messages?: {
        id: number;
        user_id: string;
        username: string;
        avatar_url?: string; 
        user_role?: string;
        message: string;
        created_at: string;
    }[];
}

interface AppliedPenalty {
    userId: string;
    ruleId: string;
    points: number;
    description: string;
    isWarning: boolean;
    timePenalty: number;
    gridPenalty: number;
    tempId: number;
}

interface PenaltyRecord {
    id: number;
    username: string;
    track_location: string;
    points: number;
    time_penalty: number;
    grid_penalty: number;
    warning: boolean;
    description: string;
    created_at: string;
    rule_id?: string;
}

// --- VIDEO PARSER HELPER ---
const parseVideoUrl = (url: string | undefined) => {
    if (!url) return null;
    // SDC Video Upload (own server)
    const sdcMatch = url.match(/video_upload\.php\?watch=([a-f0-9]+)/);
    if (sdcMatch) return { type: 'sdc', id: sdcMatch[1], url };
    const ytMatch = url.match(/^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/);
    if (ytMatch && ytMatch[2].length === 11) return { type: 'youtube', id: ytMatch[2] };
    const stMatch = url.match(/streamable\.com\/([a-zA-Z0-9]+)/);
    if (stMatch) return { type: 'streamable', id: stMatch[1] };
    const twClipMatch = url.match(/(?:twitch\.tv\/.*\/clip\/|clips\.twitch\.tv\/)([a-zA-Z0-9_-]+)/);
    if (twClipMatch) return { type: 'twitch_clip', id: twClipMatch[1] };
    const twVidMatch = url.match(/twitch\.tv\/videos\/(\d+)/);
    if (twVidMatch) return { type: 'twitch_vod', id: twVidMatch[1] };
    // Direct video file URLs
    if (url.match(/\.(mp4|webm|mov)(\?|$)/i)) return { type: 'direct', id: '', url };
    return null;
};

const VideoEmbed: React.FC<{ url: string }> = ({ url }) => {
    const video = parseVideoUrl(url);
    const hostname = window.location.hostname;
    if (video) {
        // SDC own videos or direct video files - native player
        if (video.type === 'sdc' || video.type === 'direct') {
            const videoSrc = video.type === 'sdc' 
                ? url.replace('video_upload.php?watch=', 'uploads/videos/') + '.mp4'
                : url;
            return (
                <div className="my-2">
                    <div className="relative w-full bg-black rounded-lg overflow-hidden border border-gray-700 shadow-lg">
                        <iframe src={video.type === 'sdc' ? url : undefined} className={video.type === 'sdc' ? 'w-full aspect-video' : 'hidden'} frameBorder="0" allowFullScreen style={{display: video.type === 'sdc' ? 'block' : 'none'}}/>
                        {video.type === 'direct' && <video controls className="w-full" preload="metadata"><source src={url}/></video>}
                    </div>
                    {video.type === 'sdc' && <a href={url} target="_blank" rel="noreferrer" className="text-[9px] text-gray-600 hover:text-gray-400 mt-1 inline-flex items-center gap-1"><ExternalLink size={9}/> In neuem Tab öffnen</a>}
                </div>
            );
        }
        let src = '';
        if (video.type === 'youtube') src = `https://www.youtube.com/embed/${video.id}`;
        else if (video.type === 'streamable') src = `https://streamable.com/e/${video.id}`;
        else if (video.type === 'twitch_clip') src = `https://clips.twitch.tv/embed?clip=${video.id}&parent=${hostname}`;
        else if (video.type === 'twitch_vod') src = `https://player.twitch.tv/?video=${video.id}&parent=${hostname}`;
        return <div className="relative w-full pb-[56.25%] bg-black rounded overflow-hidden border border-gray-700 shadow-lg my-2"><iframe className="absolute top-0 left-0 w-full h-full" src={src} frameBorder="0" allowFullScreen></iframe></div>;
    }
    return <a href={url} target="_blank" rel="noreferrer" className="block bg-f1-red/10 border border-f1-red/50 text-f1-red text-center py-2 rounded text-sm font-bold hover:bg-f1-red hover:text-white transition-colors mt-2"><ExternalLink size={16} className="inline mr-2"/> Externen Link öffnen</a>;
};

const renderMessageContent = (text: string) => {
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    const mentionRegex = /(@[a-zA-Z0-9._-]+)/g;
    
    // Split by both URLs and Mentions
    const parts = text.split(/(https?:\/\/[^\s]+|@[a-zA-Z0-9._-]+)/g);
    
    const matches = text.match(urlRegex) || [];
    const videoLinks = matches.filter(url => parseVideoUrl(url) !== null);
    
    return (
        <div>
            <p className="whitespace-pre-wrap break-words">
                {parts.map((part, i) => {
                    if (urlRegex.test(part)) {
                        return <a key={i} href={part} target="_blank" rel="noreferrer" className="text-blue-400 hover:underline">{part}</a>;
                    }
                    if (mentionRegex.test(part)) {
                        return <span key={i} className="text-f1-red font-bold bg-f1-red/10 px-1 rounded">{part}</span>;
                    }
                    return part;
                })}
            </p>
            {videoLinks.map((url, i) => (<div key={i} className="mt-2 max-w-[400px]"><VideoEmbed url={url} /></div>))}
        </div>
    );
};

// --- Helper for Date Display ---
const getTicketDate = (t: any) => {
    // Try standard props first
    const raw = t.created_at || t.createdAt;
    if (!raw) return 'Datum unbekannt';
    const d = new Date(raw);
    if (isNaN(d.getTime())) return 'Datum ungültig';
    return d.toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

// --- Ticket Detail Component ---
const TicketDetail: React.FC<{ 
    ticket: TicketExtended; 
    user: User | null;
    drivers: User[];
    isSteward: boolean;
    onClose: () => void;
    onUpdateStatus: (status: TicketStatus, decision?: string, penalties?: any[], postToNews?: boolean) => Promise<void>;
    onSendMessage: (text: string) => Promise<void>;
    onInvite: (userId: string) => Promise<void>;
    onDelete: () => Promise<void>;
}> = ({ ticket, user, drivers, isSteward, onClose, onUpdateStatus, onSendMessage, onInvite, onDelete }) => {
    
    const { rules } = useLeague(); 
    const [newMessage, setNewMessage] = useState('');
    const [decisionText, setDecisionText] = useState(ticket.decision || '');
    const [inviteUserId, setInviteUserId] = useState('');
    const [postToNews, setPostToNews] = useState(true); 
    const [selectedGroup, setSelectedGroup] = useState<string>('');
    const [selectedRuleId, setSelectedRuleId] = useState<string>('');
    const [penaltyTargetId, setPenaltyTargetId] = useState<string>('');
    const [appliedPenalties, setAppliedPenalties] = useState<AppliedPenalty[]>([]);
    
    // NEW: PING SELECTION
    const [pingSelection, setPingSelection] = useState<string[]>([]);
    
    // NEW: MENTION STATE
    const [showMentions, setShowMentions] = useState(false);
    const [mentionFilter, setMentionFilter] = useState('');
    const [mentionIndex, setMentionIndex] = useState(-1);
    const inputRef = useRef<HTMLTextAreaElement>(null);
    
    const chatEndRef = useRef<HTMLDivElement>(null);
    const accused = ticket.participants?.find(p => p.role === 'ACCUSED');

    const isLocked = ticket.discussionLocked; 
    const isPrivateReview = ticket.status === TicketStatus.IN_REVIEW_PRIVATE || ticket.status === TicketStatus.DECISION_PENDING;
    const validVideoUrl = ticket.videoUrl || ticket.video_url;
    const isArchived = ticket.status === TicketStatus.PUBLISHED || ticket.status === TicketStatus.CLOSED;
    const hideChatContent = isPrivateReview && !isSteward;
    const canSend = !isLocked && !isArchived && !hideChatContent;

    // Timer Logic
    const [timeLeft, setTimeLeft] = useState<string>('');
    useEffect(() => {
        const calculateTime = () => {
            const created = new Date(ticket.created_at || ticket.createdAt);
            const deadline = new Date(created.getTime() + 24 * 60 * 60 * 1000);
            const now = new Date();
            const diff = deadline.getTime() - now.getTime();
            
            if (diff <= 0 || isArchived) {
                setTimeLeft('');
                return;
            }
            
            const h = Math.floor(diff / (1000 * 60 * 60));
            const m = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
            const s = Math.floor((diff % (1000 * 60)) / 1000);
            setTimeLeft(`${h}h ${m}m ${s}s`);
        };
        
        calculateTime();
        const interval = setInterval(calculateTime, 1000);
        return () => clearInterval(interval);
    }, [ticket, isArchived]);

    useEffect(() => {
        chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
        if (accused) setPenaltyTargetId(accused.user_id);
        else if (ticket.participants && ticket.participants.length > 0) setPenaltyTargetId(ticket.participants[0].user_id);
    }, [ticket.messages, ticket.participants]);

    const handleSend = () => {
        if (!newMessage.trim()) return;
        onSendMessage(newMessage);
        setNewMessage('');
        setShowMentions(false);
        if (inputRef.current) {
            inputRef.current.style.height = '42px'; // Reset height
        }
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const val = e.target.value;
        setNewMessage(val);
        
        // Auto-resize
        e.target.style.height = 'inherit';
        e.target.style.height = `${Math.min(e.target.scrollHeight, 150)}px`;
        
        const lastAt = val.lastIndexOf('@');
        if (lastAt !== -1 && (lastAt === 0 || val[lastAt - 1] === ' ')) {
            const query = val.slice(lastAt + 1);
            if (!query.includes(' ')) {
                setMentionFilter(query);
                setShowMentions(true);
                setMentionIndex(0);
                return;
            }
        }
        setShowMentions(false);
    };

    const insertMention = (name: string) => {
        const lastAt = newMessage.lastIndexOf('@');
        const before = newMessage.slice(0, lastAt);
        const after = newMessage.slice(lastAt).split(' ')[1] || '';
        setNewMessage(before + '@' + name + ' ' + after);
        setShowMentions(false);
        inputRef.current?.focus();
    };

    const mentionList = [
        { id: 'all', username: 'all' },
        ...(ticket.participants || []).map(p => ({ id: p.user_id, username: p.username })),
        ...drivers.filter(d => !ticket.participants?.some(p => p.user_id === d.id))
    ].filter(m => m.username.toLowerCase().includes(mentionFilter.toLowerCase())).slice(0, 8);

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (showMentions) {
            if (e.key === 'ArrowDown') {
                e.preventDefault();
                setMentionIndex(prev => (prev + 1) % mentionList.length);
            } else if (e.key === 'ArrowUp') {
                e.preventDefault();
                setMentionIndex(prev => (prev - 1 + mentionList.length) % mentionList.length);
            } else if (e.key === 'Enter' || e.key === 'Tab') {
                e.preventDefault();
                if (mentionList[mentionIndex]) insertMention(mentionList[mentionIndex].username);
            } else if (e.key === 'Escape') {
                setShowMentions(false);
            }
        } else if (e.key === 'Enter' && e.ctrlKey) {
            e.preventDefault();
            handleSend();
        }
    };

    const handlePublishDecision = () => {
        if(!confirm("Entscheidung veröffentlichen? Das Ticket wird archiviert.")) return;
        onUpdateStatus(TicketStatus.PUBLISHED, decisionText, appliedPenalties, postToNews);
    };

    const handleInvite = async () => {
        if (!inviteUserId) return;
        await onInvite(inviteUserId);
        setInviteUserId('');
    };

    const addPenalty = () => {
        if (!selectedRuleId || !penaltyTargetId) return;
        let rule: any = null;
        for(const grp of rules) {
            const found = grp.items.find(i => i.id === selectedRuleId);
            if(found) { rule = found; break; }
        }
        if(!rule) return;
        const targetUser = ticket.participants?.find(p => p.user_id === penaltyTargetId);
        const targetName = targetUser ? targetUser.username : 'Unbekannt';
        let desc = `§${rule.id} ${rule.label}`;
        if(rule.points && rule.points < 0) desc += ` (${rule.points} Pkt)`;

        const newP: AppliedPenalty = {
            userId: penaltyTargetId,
            ruleId: rule.id,
            points: rule.points || 0,
            description: desc,
            isWarning: false,
            timePenalty: 0,
            gridPenalty: 0,
            tempId: Date.now()
        };
        setAppliedPenalties([...appliedPenalties, newP]);
        setDecisionText((decisionText ? decisionText + "\n\n" : "") + `**Strafe gegen ${targetName}:**\n${desc}`);
        setSelectedRuleId('');
    };
    
    // NEW: HANDLE PING
    const togglePing = (userId: string) => {
        if (pingSelection.includes(userId)) {
            setPingSelection(prev => prev.filter(id => id !== userId));
        } else {
            setPingSelection(prev => [...prev, userId]);
        }
    };
    
    const sendPings = async () => {
        if (pingSelection.length === 0) return;
        try {
            await apiRequest('tickets.php', 'POST', {
                action: 'ping_participants',
                ticketId: ticket.id,
                userIds: pingSelection,
                actingUserId: user?.id
            });
            alert("Erinnerung via Discord gesendet!");
            setPingSelection([]);
        } catch(e: any) {
            alert(e.message);
        }
    };

    const getUrl = (url?: string) => {
        if (!url) return undefined;
        if (url.startsWith('http') || url.startsWith('data:')) return url;
        const baseUrl = API_BASE_URL === '.' ? '' : API_BASE_URL;
        return baseUrl ? `${baseUrl}/${url}` : url;
    };

    const getRoleBorderColor = (rolesStr?: string) => {
        if (!rolesStr) return 'border-gray-600';
        const roles = rolesStr.split(',');
        if (roles.includes('WEBADMIN') || roles.includes('ADMIN')) return 'border-f1-red';
        if (roles.includes('STEWARD')) return 'border-yellow-500';
        return 'border-gray-600';
    };
    
    const getParticipantColor = (role: string) => {
        if (role === 'ACCUSED') return 'border-red-500 text-red-500';
        if (role === 'ACCUSER') return 'border-blue-500 text-blue-400';
        return 'border-gray-500 text-gray-400';
    };

    const filteredRules = selectedGroup ? rules.find(g => g.group === selectedGroup)?.items || [] : [];
    
    return (
        <div className="space-y-6 animate-fade-in">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center border-b border-gray-800 pb-4 gap-4">
                <div>
                    <div className="flex items-center gap-3 mb-2">
                        <h2 className="text-2xl font-bold uppercase text-white">Ticket #{ticket.id}</h2>
                        <Badge color={isArchived ? 'green' : 'yellow'}>{ticket.status.replace(/_/g, ' ')}</Badge>
                        {timeLeft && (
                            <div className="flex items-center gap-2 bg-f1-red/20 border border-f1-red/50 px-3 py-1 rounded animate-pulse">
                                <Timer size={14} className="text-f1-red"/>
                                <span className="text-xs font-mono font-bold text-white">Deadline: {timeLeft}</span>
                            </div>
                        )}
                    </div>
                    <div className="text-sm text-gray-400 flex flex-wrap gap-4">
                        <span className="flex items-center gap-1"><Clock size={14}/> {getTicketDate(ticket)}</span>
                        <span className="flex items-center gap-1"><Flag size={14}/> {ticket.trackLocation}</span>
                    </div>
                </div>
                <div className="flex gap-2">
                    {isSteward && <Button variant="danger" size="sm" onClick={onDelete} icon={Trash2}>Löschen</Button>}
                    <Button variant="secondary" size="sm" onClick={onClose}>Schließen</Button>
                </div>
            </div>

            {(ticket.status === 'PUBLISHED' || ticket.status === 'CLOSED') && ticket.decision && (
                <div className="bg-green-900/20 border border-green-600/50 p-4 rounded shadow-lg">
                    <h3 className="text-green-400 font-bold uppercase text-sm mb-2 flex items-center gap-2"><Gavel size={16}/> Urteil / Begründung</h3>
                    <p className="text-white whitespace-pre-wrap">{ticket.decision}</p>
                </div>
            )}
            
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-1 space-y-4">
                    <div className="bg-f1-carbon p-4 rounded border border-gray-800 shadow-lg">
                        <h3 className="text-gray-500 text-xs font-bold uppercase mb-4 border-b border-gray-800 pb-2">Vorfall Details</h3>
                        <div className="space-y-4">
                            <div className="flex justify-between items-center"><span className="text-gray-400 text-sm">Melder</span><span className="font-bold text-white">{ticket.authorName}</span></div>
                            {accused && <div className="flex justify-between items-center"><span className="text-gray-400 text-sm">Beschuldigter</span><span className="font-bold text-f1-red">{accused.username}</span></div>}
                            <div className="grid grid-cols-2 gap-2 bg-gray-900 p-2 rounded">
                                <div><span className="text-[10px] text-gray-500 block uppercase">Runde</span><span className="font-mono font-bold">{ticket.lap || '-'}</span></div>
                                <div><span className="text-[10px] text-gray-500 block uppercase">Kurve</span><span className="font-mono font-bold">{ticket.corner || '-'}</span></div>
                            </div>
                            <div><span className="text-gray-500 text-xs block mb-1 uppercase">Beschreibung</span><p className="text-sm bg-gray-900 p-3 rounded text-gray-300 italic whitespace-pre-wrap">"{ticket.description}"</p></div>
                            {validVideoUrl && <div className="pt-2 border-t border-gray-800"><span className="text-f1-red text-xs font-bold block mb-1 uppercase flex items-center gap-1"><Video size={12}/> Beweismaterial</span><VideoEmbed url={validVideoUrl} /></div>}
                        </div>
                    </div>
                    
                    <div className="bg-gray-800 border border-gray-700 p-4 rounded shadow-lg">
                        <div className="flex justify-between items-center mb-3">
                            <h3 className="text-gray-400 text-xs font-bold uppercase flex items-center">
                                <Users size={14} className="mr-2"/> Beteiligte
                            </h3>
                            {isSteward && pingSelection.length > 0 && (
                                <button onClick={sendPings} className="text-[10px] bg-blue-600 text-white px-2 py-1 rounded hover:bg-blue-500 flex items-center gap-1 animate-pulse">
                                    <BellRing size={10}/> Erinnern ({pingSelection.length})
                                </button>
                            )}
                        </div>
                        
                        <div className="space-y-2 mb-4">
                            {ticket.participants?.map(p => {
                                const styleClass = getParticipantColor(p.role);
                                const isSelected = pingSelection.includes(p.user_id);
                                
                                return (
                                    <div key={p.user_id} className={`flex items-center gap-3 bg-gray-900 p-2 rounded border-l-2 ${p.role === 'ACCUSED' ? 'border-l-red-500' : p.role === 'ACCUSER' ? 'border-l-blue-500' : 'border-l-gray-500'}`}>
                                        {isSteward && (
                                            <input 
                                                type="checkbox" 
                                                checked={isSelected}
                                                onChange={() => togglePing(p.user_id)}
                                                className="w-4 h-4 rounded bg-gray-800 border-gray-600 text-f1-red cursor-pointer"
                                            />
                                        )}
                                        <div className={`w-8 h-8 rounded-full bg-gray-800 overflow-hidden flex items-center justify-center shrink-0 border ${styleClass.split(' ')[0]}`}>
                                            {p.avatar_url ? (
                                                <img src={getUrl(p.avatar_url)} className="w-full h-full object-cover" />
                                            ) : (
                                                <UserIcon size={14} className="text-gray-400"/>
                                            )}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="text-white font-bold text-sm truncate">{p.username}</div>
                                            <div className={`text-[10px] font-bold uppercase ${styleClass.split(' ')[1]}`}>
                                                {p.role === 'ACCUSER' ? 'Melder' : p.role === 'ACCUSED' ? 'Beschuldigt' : 'Zeuge'}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                        {isSteward && !isPrivateReview && (
                            <div className="border-t border-gray-700 pt-3">
                                <div className="flex gap-2">
                                    <select 
                                        className="bg-gray-900 border border-gray-600 rounded p-1 text-xs text-white flex-1"
                                        value={inviteUserId}
                                        onChange={e => setInviteUserId(e.target.value)}
                                    >
                                        <option value="">Fahrer hinzufügen...</option>
                                        {drivers.map(d => (
                                            <option key={d.id} value={d.id}>{d.username}</option>
                                        ))}
                                    </select>
                                    <Button size="sm" onClick={handleInvite} disabled={!inviteUserId}>+</Button>
                                </div>
                            </div>
                        )}
                    </div>
                    
                    {isSteward && (
                         <div className="bg-gray-800 border border-gray-700 p-4 rounded shadow-lg">
                              <h3 className="text-f1-red text-xs font-bold uppercase mb-3 flex items-center"><Gavel size={14} className="mr-2"/> Stewards Panel</h3>
                              <div className="space-y-4">
                                  <div className="grid grid-cols-1 gap-2">
                                      {!isPrivateReview ? (
                                          <Button variant="secondary" onClick={() => onUpdateStatus(TicketStatus.IN_REVIEW_PRIVATE, decisionText)} className="w-full bg-gray-700 border-l-4 border-yellow-500"><Lock size={14} className="mr-2"/> Untersuchung starten (Privat)</Button>
                                      ) : (
                                          <Button variant="ghost" size="sm" onClick={() => onUpdateStatus(TicketStatus.OPEN_PUBLIC, decisionText)} className="w-full text-gray-400"><Unlock size={14} className="mr-2"/> Fall wieder öffnen</Button>
                                      )}
                                  </div>
                                  {isPrivateReview && (
                                      <div className="animate-fade-in space-y-4 pt-4 border-t border-gray-700">
                                          <div className="bg-gray-900 p-3 rounded border border-gray-700">
                                              <select className="w-full bg-gray-800 border border-gray-600 rounded p-1.5 text-sm text-white mb-2" value={penaltyTargetId} onChange={e => setPenaltyTargetId(e.target.value)}>
                                                  <option value="">-- Ziel der Strafe --</option>
                                                  {ticket.participants?.map(p => <option key={p.user_id} value={p.user_id}>{p.username} ({p.role})</option>)}
                                              </select>
                                              <select className="w-full bg-gray-800 border border-gray-600 rounded p-1.5 text-sm text-white mb-2" value={selectedGroup} onChange={e => setSelectedGroup(e.target.value)}>
                                                  <option value="">-- Strafenkatalog --</option>
                                                  {rules.map(g => <option key={g.group} value={g.group}>{g.group}</option>)}
                                              </select>
                                              {selectedGroup && (
                                                  <select className="w-full bg-gray-800 border border-gray-600 rounded p-1.5 text-sm text-white mb-2 truncate" value={selectedRuleId} onChange={e => setSelectedRuleId(e.target.value)}>
                                                      <option value="">-- Regel wählen --</option>
                                                      {filteredRules.map(r => <option key={r.id} value={r.id}>{r.id} - {r.label}</option>)}
                                                  </select>
                                              )}
                                              <Button size="sm" variant="secondary" onClick={addPenalty} disabled={!selectedRuleId || !penaltyTargetId} className="w-full"><Plus size={14} className="mr-1"/> Strafe hinzufügen</Button>
                                              {appliedPenalties.length > 0 && <div className="space-y-1 mt-2">{appliedPenalties.map(p => <div key={p.tempId} className="text-xs bg-red-900/20 border border-red-900 rounded p-1.5 flex justify-between text-red-200"><span className="truncate">{p.description}</span><button onClick={() => setAppliedPenalties(prev => prev.filter(x => x.tempId !== p.tempId))}><XCircle size={12}/></button></div>)}</div>}
                                          </div>
                                          <textarea className="w-full bg-gray-900 border border-gray-700 rounded p-2 text-sm text-white h-24 focus:border-f1-red outline-none" placeholder="Begründung..." value={decisionText} onChange={e => setDecisionText(e.target.value)}></textarea>
                                          <div className="flex items-center gap-2 py-2"><input type="checkbox" checked={postToNews} onChange={(e) => setPostToNews(e.target.checked)} className="w-4 h-4 rounded bg-gray-900 border-gray-600 text-f1-red"/><label className="text-sm text-gray-300 cursor-pointer">Als News posten?</label></div>
                                          <Button variant="primary" size="sm" onClick={handlePublishDecision} className="w-full"><CheckCircle size={14} className="mr-1"/> Fall abschließen</Button>
                                      </div>
                                  )}
                              </div>
                         </div>
                     )}
                </div>
                <div className="lg:col-span-2 flex flex-col h-[600px] bg-f1-carbon rounded border border-gray-800 shadow-lg relative overflow-hidden">
                    {hideChatContent ? (
                        <div className="absolute inset-0 z-20 bg-gray-900 flex flex-col items-center justify-center text-center p-8">
                            <div className="w-20 h-20 bg-gray-800 rounded-full flex items-center justify-center mb-6 border-2 border-red-500"><Lock size={40} className="text-red-500" /></div>
                            <h2 className="text-3xl font-bold uppercase italic text-white mb-2">In Bearbeitung</h2>
                            <p className="text-gray-400 max-w-md">Stewards beraten sich gerade. Der Chat ist für Fahrer gesperrt.</p>
                        </div>
                    ) : (
                        <>
                            <div className="p-3 border-b border-gray-800 bg-gray-900/50 flex justify-between items-center">
                                <span className="font-bold text-sm text-gray-400 uppercase tracking-wider flex items-center gap-2"><MessageSquare size={16}/> Diskussion</span>
                                {(isLocked || isArchived) && <Badge color="gray">Read-Only</Badge>}
                            </div>
                            <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-dots">
                                {ticket.messages && ticket.messages.length > 0 ? (
                                    ticket.messages.map(msg => {
                                        const isMe = msg.user_id === user?.id;
                                        const roleBorder = getRoleBorderColor(msg.user_role);
                                        const isStewardMsg = msg.user_role?.includes('STEWARD') || msg.user_role?.includes('ADMIN');
                                        
                                        // DATE in Chat
                                        const date = new Date(msg.created_at);
                                        const dateStr = date.toLocaleString('de-DE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });

                                        return (
                                            <div key={msg.id} className={`flex flex-col ${isMe ? 'items-end' : 'items-start'}`}>
                                                <div className={`flex items-end gap-2 mb-1 ${isMe ? 'flex-row-reverse' : 'flex-row'}`}>
                                                    <div className={`w-6 h-6 rounded-full bg-gray-800 border-2 overflow-hidden flex items-center justify-center shrink-0 ${roleBorder}`}>
                                                        {msg.avatar_url ? <img src={getUrl(msg.avatar_url)} className="w-full h-full object-cover" /> : <UserIcon size={12} className="text-gray-400"/>}
                                                    </div>
                                                    <div className="flex items-baseline gap-2">
                                                        <span className={`text-xs font-bold ${isStewardMsg ? 'text-f1-red' : 'text-gray-400'}`}>{msg.username} {isStewardMsg && '(Team)'}</span>
                                                        <span className="text-[10px] text-gray-600">{dateStr}</span>
                                                    </div>
                                                </div>
                                                <div className={`p-3 rounded-lg max-w-[90%] text-sm leading-relaxed shadow-sm ${isMe ? 'bg-blue-900/30 text-blue-100 rounded-tr-none border border-blue-800/50' : isStewardMsg ? 'bg-red-900/20 text-red-100 rounded-tl-none border border-red-800/50' : 'bg-gray-800 text-gray-200 rounded-tl-none border border-gray-700'}`}>{renderMessageContent(msg.message)}</div>
                                            </div>
                                        );
                                    })
                                ) : <div className="text-center text-gray-500 italic mt-10">Noch keine Nachrichten.</div>}
                                <div ref={chatEndRef} />
                            </div>
                            {canSend && (
                                <div className="p-4 border-t border-gray-800 bg-gray-900/80 relative">
                                    {showMentions && mentionList.length > 0 && (
                                        <div className="absolute bottom-full left-4 mb-2 w-64 bg-gray-800 border border-gray-700 rounded shadow-2xl z-50 overflow-hidden">
                                            <div className="p-2 bg-gray-900 border-b border-gray-700 text-[10px] text-gray-500 uppercase font-bold">Fahrer erwähnen</div>
                                            {mentionList.map((m, i) => (
                                                <div 
                                                    key={m.id} 
                                                    className={`px-3 py-2 text-sm cursor-pointer flex items-center gap-2 ${i === mentionIndex ? 'bg-f1-red text-white' : 'text-gray-300 hover:bg-gray-700'}`}
                                                    onClick={() => insertMention(m.username)}
                                                >
                                                    <span className="font-bold">@{m.username}</span>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                    <div className="flex gap-2 items-end">
                                        <textarea 
                                            ref={inputRef}
                                            className="flex-1 bg-gray-800 border border-gray-700 rounded px-4 py-2 text-white focus:border-f1-red outline-none transition-colors resize-none h-[42px] min-h-[42px] max-h-32 custom-scrollbar" 
                                            placeholder="Nachricht schreiben... (@ für Erwähnungen, Ctrl+Enter zum Senden)" 
                                            value={newMessage} 
                                            onChange={handleInputChange} 
                                            onKeyDown={handleKeyDown}
                                        />
                                        <Button onClick={handleSend} disabled={!newMessage.trim()} className="h-[42px]"><Send size={18}/></Button>
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export const TicketSystem: React.FC = () => {
    const { user } = useAuth();
    const { drivers, rules } = useLeague(); // Rules from Context
    const [tickets, setTickets] = useState<TicketExtended[]>([]);
    const [events, setEvents] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    
    // NEW: View Ticket State (Full Data)
    const [viewTicket, setViewTicket] = useState<TicketExtended | null>(null);
    const [activeTab, setActiveTab] = useState<'TICKETS' | 'MANUAL' | 'NEWS_GEN' | 'ARCHIVE'>('TICKETS');
    const [viewMode, setViewMode] = useState<'OPEN' | 'ARCHIVE'>('OPEN'); // For Ticket List only
    
    // Ticket Creation State
    const [showCreate, setShowCreate] = useState(false);
    const [createData, setCreateData] = useState({
        eventId: '', type: 'INCIDENT', accusedDriverId: '', lap: '', corner: '', description: '', videoUrl: ''
    });

    // Manual Penalty State
    const [manEventId, setManEventId] = useState('');
    const [manDriverId, setManDriverId] = useState('');
    const [manGroup, setManGroup] = useState('');
    const [manRuleId, setManRuleId] = useState('');
    const [manPoints, setManPoints] = useState(0);
    const [manTime, setManTime] = useState(0);
    const [manGrid, setManGrid] = useState(0);
    const [manWarning, setManWarning] = useState(false);
    const [manDesc, setManDesc] = useState('');

    // News Generator State
    const [genPenalties, setGenPenalties] = useState<PenaltyRecord[]>([]);
    const [selectedPenalties, setSelectedPenalties] = useState<number[]>([]);
    const [generatedNews, setGeneratedNews] = useState('');
    const [newsTitle, setNewsTitle] = useState('Entscheidungen der Rennleitung');

    const isSteward = user?.roles.includes(UserRole.STEWARD) || user?.roles.includes(UserRole.ADMIN) || user?.roles.includes(UserRole.WEBADMIN);

    const loadList = async () => {
        if (!user) return;
        setLoading(true);
        try {
            // Only loads list summary, NO messages
            const [tData, eData] = await Promise.all([
                apiRequest<TicketExtended[]>(`tickets.php?userId=${user.id}`), 
                apiRequest<any[]>('events.php')
            ]);
            setTickets(Array.isArray(tData) ? tData : []);
            if(Array.isArray(eData)) {
                // MODIFIED: Show all events sorted by date descending
                const allEvents = eData.sort((a,b) => new Date(b.date).getTime() - new Date(a.date).getTime());
                setEvents(allEvents);
                
                // Default event selection for manual
                if(allEvents.length > 0 && !manEventId) setManEventId(allEvents[0].id);
            }
        } catch (e) { console.error(e); } 
        finally { setLoading(false); }
    };

    useEffect(() => { loadList(); }, [user]);

    // LOAD PENALTIES FOR GENERATOR
    useEffect(() => {
        if (activeTab === 'NEWS_GEN') {
            apiRequest<PenaltyRecord[]>('penalties.php').then(res => {
                setGenPenalties(Array.isArray(res) ? res : []);
            });
        }
    }, [activeTab]);

    // NEW: Fetch full details including messages when clicking a ticket
    const openTicket = async (id: string) => {
        setLoading(true);
        try {
            const res = await apiRequest<any>(`tickets.php?id=${id}&userId=${user?.id}`);
            const ticketData = res.data || res;
            setViewTicket(ticketData);
        } catch (e: any) {
            alert("Fehler beim Laden des Tickets: " + e.message);
        } finally {
            setLoading(false);
        }
    };

    const handleCreate = async () => {
        if (!createData.eventId || !createData.description) { alert("Bitte Event und Beschreibung ausfüllen."); return; }
        try {
            await apiRequest('tickets.php', 'POST', { ...createData, authorId: user?.id });
            setShowCreate(false);
            setCreateData({ eventId: '', type: 'INCIDENT', accusedDriverId: '', lap: '', corner: '', description: '', videoUrl: '' });
            loadList();
        } catch (e: any) { alert(e.message); }
    };

    const handleUpdateStatus = async (ticketId: string, status: TicketStatus, decision?: string, penalties?: any[], postToNews?: boolean) => {
        try {
            await apiRequest('tickets.php', 'PUT', { id: ticketId, status, decision, penalties, postToNews });
            
            // Reload list and update current view if open
            if(viewTicket && viewTicket.id === ticketId) {
                openTicket(ticketId); // Refresh details
            }
            loadList();
        } catch (e: any) { alert(e.message); }
    };

    const handleSendMessage = async (ticketId: string, message: string) => {
        try {
            await apiRequest('tickets.php', 'POST', { action: 'message', ticketId, userId: user?.id, message });
            openTicket(ticketId); // Refresh messages
        } catch (e: any) { alert(e.message); }
    };

    const handleInvite = async (ticketId: string, userId: string) => {
        try {
            await apiRequest('tickets.php', 'POST', { action: 'invite', ticketId, userId });
            openTicket(ticketId);
        } catch (e: any) { alert(e.message); }
    };

    const handleDelete = async (ticketId: string) => {
        if(!confirm("Ticket löschen?")) return;
        try {
            await apiRequest(`tickets.php?id=${ticketId}`, 'DELETE');
            setViewTicket(null);
            loadList();
        } catch (e: any) { alert(e.message); }
    };
    
    // --- MANUAL PENALTY LOGIC ---
    const handleManualPenalty = async () => {
        if (!manDriverId || !manEventId || !manDesc) { alert("Bitte Event, Fahrer und Grund angeben."); return; }
        try {
            await apiRequest('penalties.php', 'POST', {
                userId: manDriverId,
                eventId: manEventId,
                ruleId: manRuleId,
                description: manDesc,
                points: manPoints,
                timePenalty: manTime,
                gridPenalty: manGrid,
                isWarning: manWarning
            });
            alert("Strafe eingetragen.");
            // Reset essential
            setManPoints(0); setManTime(0); setManGrid(0); setManWarning(false); setManDesc('');
        } catch(e: any) { alert(e.message); }
    };

    // Auto-Fill description if rule selected
    useEffect(() => {
        if (manGroup && manRuleId) {
            const grp = rules.find(g => g.group === manGroup);
            const r = grp?.items.find(i => i.id === manRuleId);
            if (r) {
                setManDesc(r.label);
                setManPoints(r.points || 0);
            }
        }
    }, [manRuleId]);

    // --- NEWS GENERATOR LOGIC ---
    const generateNewsText = () => {
        const selected = genPenalties.filter(p => selectedPenalties.includes(p.id));
        if(selected.length === 0) return;
        
        // Group by Event
        const byEvent: Record<string, PenaltyRecord[]> = {};
        selected.forEach(p => {
            const evtName = p.track_location || 'Event';
            if(!byEvent[evtName]) byEvent[evtName] = [];
            byEvent[evtName].push(p);
        });

        let text = "";
        Object.keys(byEvent).forEach(evt => {
            text += `**${evt}**\n`;
            byEvent[evt].forEach(p => {
                let penStr = "";
                if(p.points < 0) penStr += `${p.points} Pkt `;
                if(p.time_penalty > 0) penStr += `+${p.time_penalty} Sek `;
                if(p.grid_penalty > 0) penStr += `+${p.grid_penalty} Grid `;
                if(p.warning) penStr += `(Verwarnung) `;
                if(!penStr) penStr = "Strafe ";
                
                text += `• **${p.username}**: ${penStr.trim()} - ${p.description}\n`;
            });
            text += "\n";
        });
        
        text += "Bitte achtet auf Fair Play!";
        setGeneratedNews(text);
    };

    const postNews = async () => {
        if(!generatedNews) return;
        try {
            await apiRequest('news.php', 'POST', {
                title: newsTitle,
                content: generatedNews,
                category: 'DECISION',
                author_id: user?.id
            });
            alert("News veröffentlicht!");
            setGeneratedNews('');
            setSelectedPenalties([]);
        } catch(e: any) { alert(e.message); }
    };

    // Filter logic
    const filteredTickets = tickets.filter(t => {
        const isClosed = t.status === TicketStatus.CLOSED || t.status === TicketStatus.PUBLISHED;
        return viewMode === 'ARCHIVE' ? isClosed : !isClosed;
    });

    if (viewTicket) {
        return (
            <TicketDetail 
                ticket={viewTicket} 
                user={user} 
                drivers={drivers}
                isSteward={isSteward}
                onClose={() => { setViewTicket(null); loadList(); }}
                onUpdateStatus={(s, d, p, n) => handleUpdateStatus(viewTicket.id, s, d, p, n)}
                onSendMessage={(m) => handleSendMessage(viewTicket.id, m)}
                onInvite={(uid) => handleInvite(viewTicket.id, uid)}
                onDelete={() => handleDelete(viewTicket.id)}
            />
        );
    }

    return (
        <div className="mx-auto max-w-7xl space-y-6 animate-fade-in pb-20">
            <PageHero
                eyebrow="Race Control"
                title="Rennleitung"
                description="Vorfälle melden, Entscheidungen nachvollziehen und die Kommunikation rund um Proteste zentral verwalten."
                icon={Gavel}
                accent="#e10600"
                stats={[
                    { label: 'Offen', value: tickets.filter(t => t.status !== TicketStatus.CLOSED && t.status !== TicketStatus.PUBLISHED).length },
                    { label: 'Archiviert', value: tickets.filter(t => t.status === TicketStatus.CLOSED || t.status === TicketStatus.PUBLISHED).length },
                    { label: 'Ansicht', value: viewMode === 'OPEN' ? 'Aktuell' : 'Archiv' },
                ]}
                actions={(
                    <div className="flex w-full gap-2 overflow-x-auto md:w-auto">
                    <button onClick={() => setActiveTab('TICKETS')} className={`px-4 py-2 rounded text-xs font-bold uppercase flex items-center gap-2 transition-colors ${activeTab==='TICKETS'?'bg-f1-red text-white':'text-gray-400 hover:text-white'}`}><Inbox size={14}/> Tickets</button>
                    {isSteward && <button onClick={() => setActiveTab('MANUAL')} className={`px-4 py-2 rounded text-xs font-bold uppercase flex items-center gap-2 transition-colors ${activeTab==='MANUAL'?'bg-f1-red text-white':'text-gray-400 hover:text-white'}`}><FilePlus size={14}/> Direktstrafe</button>}
                    {isSteward && <button onClick={() => setActiveTab('NEWS_GEN')} className={`px-4 py-2 rounded text-xs font-bold uppercase flex items-center gap-2 transition-colors ${activeTab==='NEWS_GEN'?'bg-f1-red text-white':'text-gray-400 hover:text-white'}`}><Newspaper size={14}/> News Gen</button>}
                    <Link to="/tickets/archive" className="flex-1 md:flex-none">
                        <Button variant="secondary" icon={Archive} className="w-full h-full text-xs">Penalty DB</Button>
                    </Link>
                    </div>
                )}
            />
            
            {activeTab === 'TICKETS' && (
                <>
                {/* TABS */}
                <div className="mb-4 flex w-fit gap-1 rounded-[1.2rem] border border-white/[0.075] bg-[#0e1017] p-1.5">
                    <button onClick={() => setViewMode('OPEN')} className={`px-4 py-1.5 rounded text-sm font-bold uppercase transition-colors ${viewMode === 'OPEN' ? 'bg-f1-red text-white' : 'text-gray-400 hover:text-white'}`}>Aktuell</button>
                    <button onClick={() => setViewMode('ARCHIVE')} className={`px-4 py-1.5 rounded text-sm font-bold uppercase transition-colors ${viewMode === 'ARCHIVE' ? 'bg-f1-red text-white' : 'text-gray-400 hover:text-white'}`}>Archiv</button>
                </div>

                <div className="flex justify-end mb-4">
                    <Button onClick={() => setShowCreate(true)} icon={Plus}>Neues Ticket</Button>
                </div>

                {showCreate && (
                    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
                        <Card className="w-full max-w-lg bg-f1-dark border-gray-600 max-h-[90vh] overflow-y-auto">
                            <div className="flex justify-between items-center mb-4 border-b border-gray-700 pb-2">
                                <h3 className="text-xl font-bold uppercase">Vorfall melden</h3>
                                <button onClick={() => setShowCreate(false)}><XCircle className="text-gray-400 hover:text-white"/></button>
                            </div>
                            <div className="space-y-4">
                                <div>
                                    <label className="block text-gray-400 text-xs font-bold mb-2 uppercase">Rennen</label>
                                    <select className="w-full bg-gray-900 border border-gray-700 rounded p-2 text-white" value={createData.eventId} onChange={e => setCreateData({...createData, eventId: e.target.value})}>
                                        <option value="">-- Rennen wählen --</option>
                                        {events.map(e => (
                                            <option key={e.id} value={e.id}>
                                                {e.trackLocation} (R{e.roundNumber}) {e.status === 'COMPLETED' ? '[Beendet]' : ''}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-gray-400 text-xs font-bold mb-2 uppercase">Art</label>
                                        <select className="w-full bg-gray-900 border border-gray-700 rounded p-2 text-white" value={createData.type} onChange={e => setCreateData({...createData, type: e.target.value})}>
                                            <option value="INCIDENT">Incident</option>
                                            <option value="PROTEST">Protest</option>
                                            <option value="DNF">DNF Report</option>
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-gray-400 text-xs font-bold mb-2 uppercase">Gegner (Optional)</label>
                                        <select className="w-full bg-gray-900 border border-gray-700 rounded p-2 text-white" value={createData.accusedDriverId} onChange={e => setCreateData({...createData, accusedDriverId: e.target.value})}>
                                            <option value="">-- Keiner / Unbekannt --</option>
                                            {drivers.map(d => <option key={d.id} value={d.id}>{d.username}</option>)}
                                        </select>
                                    </div>
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                    <Input label="Runde" value={createData.lap} onChange={e => setCreateData({...createData, lap: e.target.value})} placeholder="z.B. 14"/>
                                    <Input label="Kurve" value={createData.corner} onChange={e => setCreateData({...createData, corner: e.target.value})} placeholder="z.B. T4"/>
                                </div>
                                <div>
                                    <label className="block text-gray-400 text-xs font-bold mb-2 uppercase">Video Beweis</label>
                                    <div className="flex gap-2">
                                        <input className="flex-1 bg-gray-900 border border-gray-700 rounded p-2.5 text-white text-sm focus:border-f1-red outline-none" value={createData.videoUrl} onChange={e => setCreateData({...createData, videoUrl: e.target.value})} placeholder="Link einfügen oder eigenes Video wählen →"/>
                                        <button type="button" onClick={async () => {
                                            try {
                                                const res = await apiRequest<any>(`video_upload.php?userId=${user?.id}`);
                                                const vids = Array.isArray(res) ? res : [];
                                                if (vids.length === 0) { alert('Keine eigenen Videos. Lade zuerst eins unter Video Upload hoch.'); return; }
                                                const options = vids.map((v: any) => `${v.original_name} (${v.days_left}d übrig)`).join('\n');
                                                const idx = prompt(`Wähle Video (Nummer 1-${vids.length}):\n\n${vids.map((v: any, i: number) => `${i+1}. ${v.original_name}`).join('\n')}`);
                                                if (idx) { const i = parseInt(idx) - 1; if (vids[i]) setCreateData({...createData, videoUrl: vids[i].watch_url}); }
                                            } catch { alert('Fehler beim Laden der Videos.'); }
                                        }} className="px-3 py-2 bg-gray-800 border border-gray-700 rounded text-gray-400 hover:text-white hover:bg-gray-700 transition-colors text-xs font-bold whitespace-nowrap" title="Eigenes Video wählen">
                                            Meine Videos
                                        </button>
                                    </div>
                                    <p className="text-[9px] text-gray-600 mt-1">YouTube, Twitch, Streamable oder eigener SDC Video Upload Link</p>
                                </div>
                                <div>
                                    <label className="block text-gray-400 text-xs font-bold mb-2 uppercase">Beschreibung</label>
                                    <textarea className="w-full bg-gray-900 border border-gray-700 rounded p-3 text-white h-24 focus:border-f1-red outline-none text-sm" placeholder="Was ist passiert?" value={createData.description} onChange={e => setCreateData({...createData, description: e.target.value})}/>
                                </div>
                            </div>
                            <div className="flex justify-end gap-2 mt-6 pt-4 border-t border-gray-700">
                                <Button variant="ghost" onClick={() => setShowCreate(false)}>Abbrechen</Button>
                                <Button onClick={handleCreate}>Ticket erstellen</Button>
                            </div>
                        </Card>
                    </div>
                )}

                <div className="grid gap-4">
                    {filteredTickets.length === 0 ? (
                        <div className="text-center p-10 text-gray-500 border-2 border-dashed border-gray-800 rounded">Keine Tickets in dieser Ansicht.</div>
                    ) : (
                        filteredTickets.map(t => (
                            <div key={t.id} onClick={() => openTicket(t.id)} className="bg-f1-carbon border border-gray-800 p-4 rounded flex flex-col md:flex-row gap-4 items-start md:items-center justify-between cursor-pointer hover:border-gray-600 transition-colors group">
                                <div className="flex items-center gap-4">
                                    <div className={`w-2 h-12 rounded-full ${t.status === 'CLOSED' || t.status === 'PUBLISHED' ? 'bg-green-500' : 'bg-yellow-500'}`}></div>
                                    <div>
                                        <div className="flex items-center gap-2 mb-1">
                                            <Badge color={t.type === 'PROTEST' ? 'red' : 'blue'}>{t.type}</Badge>
                                            <span className="text-xs text-gray-500 font-mono">#{t.id}</span>
                                            <span className="text-xs text-gray-400">{getTicketDate(t)}</span>
                                        </div>
                                        <h3 className="font-bold text-white text-lg group-hover:text-f1-red transition-colors">{t.trackLocation}</h3>
                                        <div className="text-sm text-gray-400">Von <span className="text-white">{t.authorName}</span> {t.accusedName && <span> gegen <span className="text-f1-red">{t.accusedName}</span></span>}</div>
                                    </div>
                                </div>
                                <div className="flex items-center gap-4 w-full md:w-auto justify-between md:justify-end">
                                    <div className="text-right">
                                        <div className="text-xs text-gray-500 uppercase font-bold">Status</div>
                                        <div className="font-bold text-white">{t.status.replace(/_/g, ' ')}</div>
                                    </div>
                                    <Button variant="secondary" size="sm" icon={ExternalLink}>Öffnen</Button>
                                </div>
                            </div>
                        ))
                    )}
                </div>
                </>
            )}

            {activeTab === 'MANUAL' && isSteward && (
                <Card title="Direkte Strafe vergeben (Ohne Ticket)">
                    <div className="space-y-4 max-w-2xl">
                        <div className="bg-blue-900/20 p-3 rounded border border-blue-800 text-sm text-blue-200">
                            Hier können Strafen für Vorfälle vergeben werden, die kein offizielles Ticket erfordern (z.B. Chat-Verhalten, fehlende Anwesenheit, etc.).
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-gray-400 text-xs font-bold mb-2 uppercase">Event</label>
                                <select className="w-full bg-gray-900 border border-gray-700 rounded p-2 text-white" value={manEventId} onChange={e => setManEventId(e.target.value)}>
                                    <option value="">-- Event wählen --</option>
                                    {events.map(e => (
                                        <option key={e.id} value={e.id}>
                                            {e.trackLocation} (R{e.roundNumber}) {e.status === 'COMPLETED' ? '[Beendet]' : ''}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="block text-gray-400 text-xs font-bold mb-2 uppercase">Fahrer</label>
                                <select className="w-full bg-gray-900 border border-gray-700 rounded p-2 text-white" value={manDriverId} onChange={e => setManDriverId(e.target.value)}>
                                    <option value="">-- Fahrer wählen --</option>
                                    {drivers.map(d => <option key={d.id} value={d.id}>{d.username}</option>)}
                                </select>
                            </div>
                        </div>

                        <div>
                            <label className="block text-gray-400 text-xs font-bold mb-2 uppercase">Regelverstoß (Optional Auto-Fill)</label>
                            <div className="grid grid-cols-2 gap-2">
                                <select className="w-full bg-gray-900 border border-gray-700 rounded p-2 text-white text-xs" value={manGroup} onChange={e => setManGroup(e.target.value)}>
                                    <option value="">-- Katalog --</option>
                                    {rules.map(g => <option key={g.group} value={g.group}>{g.group}</option>)}
                                </select>
                                <select className="w-full bg-gray-900 border border-gray-700 rounded p-2 text-white text-xs" value={manRuleId} onChange={e => setManRuleId(e.target.value)} disabled={!manGroup}>
                                    <option value="">-- Regel --</option>
                                    {rules.find(g => g.group === manGroup)?.items.map(i => <option key={i.id} value={i.id}>{i.id} - {i.label}</option>)}
                                </select>
                            </div>
                        </div>

                        <div className="grid grid-cols-3 gap-2">
                            <Input label="Punkte" type="number" value={manPoints} onChange={e => setManPoints(parseInt(e.target.value))}/>
                            <Input label="Zeit (Sek)" type="number" value={manTime} onChange={e => setManTime(parseInt(e.target.value))}/>
                            <Input label="Grid Pos" type="number" value={manGrid} onChange={e => setManGrid(parseInt(e.target.value))}/>
                        </div>

                        <div>
                             <label className="block text-gray-400 text-xs font-bold mb-2 uppercase">Begründung / Notiz</label>
                             <textarea className="w-full bg-gray-900 border border-gray-700 rounded p-2 text-white h-20" value={manDesc} onChange={e => setManDesc(e.target.value)} />
                        </div>
                        
                        <div className="flex items-center gap-2">
                             <input type="checkbox" checked={manWarning} onChange={e => setManWarning(e.target.checked)} className="w-4 h-4 rounded bg-gray-900 border-gray-600 text-f1-red"/>
                             <span className="text-sm font-bold text-white">Als Verwarnung markieren</span>
                        </div>

                        <Button onClick={handleManualPenalty} icon={Save} className="w-full justify-center">Strafe eintragen</Button>
                    </div>
                </Card>
            )}

            {activeTab === 'NEWS_GEN' && isSteward && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <Card title="Strafen auswählen">
                        <div className="space-y-1 max-h-[500px] overflow-y-auto pr-2 custom-scrollbar">
                             {genPenalties.length === 0 && <div className="text-gray-500 italic">Keine Strafen gefunden.</div>}
                             {genPenalties.map(p => {
                                 const isSelected = selectedPenalties.includes(p.id);
                                 return (
                                     <div key={p.id} onClick={() => setSelectedPenalties(prev => isSelected ? prev.filter(x => x !== p.id) : [...prev, p.id])} className={`p-3 rounded border cursor-pointer flex items-center justify-between ${isSelected ? 'bg-f1-red/20 border-f1-red' : 'bg-gray-900 border-gray-700 hover:border-gray-500'}`}>
                                         <div>
                                             <div className="text-xs text-gray-400">{new Date(p.created_at).toLocaleDateString()} | {p.track_location}</div>
                                             <div className="font-bold text-white">{p.username}</div>
                                             <div className="text-xs text-gray-500 truncate w-64">{p.description}</div>
                                         </div>
                                         {isSelected ? <CheckCircle size={18} className="text-f1-red"/> : <div className="w-4 h-4 rounded-full border border-gray-600"></div>}
                                     </div>
                                 )
                             })}
                        </div>
                        <div className="mt-4 pt-4 border-t border-gray-700">
                             <Button onClick={generateNewsText} disabled={selectedPenalties.length === 0} icon={RefreshCw} className="w-full justify-center">Text generieren</Button>
                        </div>
                    </Card>
                    
                    <Card title="Vorschau & Posten">
                        <Input label="Titel" value={newsTitle} onChange={e => setNewsTitle(e.target.value)} />
                        <label className="block text-gray-400 text-xs font-bold mb-2 uppercase">Inhalt</label>
                        <textarea 
                            className="w-full bg-gray-900 border border-gray-700 rounded p-3 text-white h-64 font-mono text-sm mb-4"
                            value={generatedNews}
                            onChange={e => setGeneratedNews(e.target.value)}
                            placeholder="Wähle links Strafen aus und klicke auf 'Generieren'..."
                        />
                        <div className="flex gap-2">
                             <Button variant="secondary" onClick={() => navigator.clipboard.writeText(generatedNews)} icon={Copy} disabled={!generatedNews}>Kopieren</Button>
                             <Button onClick={postNews} disabled={!generatedNews} icon={Send}>Als News posten</Button>
                        </div>
                    </Card>
                </div>
            )}
        </div>
    );
};
